import _ from "lodash"

interface EventTypeClass {
	name: string
	type?: string
}

interface EventType {
	name: string
	classes: EventTypeClass[]
}

/**
 * Push value to an array in object path. Initializes empty
 * array if path does not have a value
 *
 * @param obj 								Object to mutate
 * @param path 								Path string that is compatible with _.get
 * @param value 							Value pushed as array item
 */
const pushToPath = (obj: Record<string, unknown>, path: string | string[], value: unknown) => {
	let arrRef = _.get(obj, path)
	if (!arrRef) {
		_.set(obj, path, [])
		arrRef = _.get(obj, path)
	}
	if (!arrRef.includes(value)) {
		arrRef.push(value)
	}
	return obj
}

/**
 * Determines event type main category for exercise amount
 *
 * Decision tree
 * ET -> "trackAsGuidedExercise" -> guided
 *  \-> "trackAsOwnExercise" -> own
 *  \-> none of above -> "group" -> guided
 *                    \-> "personal -> own
 *
 * @param eventType			EventType object; requires `classes` path to be defined
 * @returns							One of ["guided", "own"]
 */
export const getExerciseMainCategory = (eventType: EventType) => {
	const hasTrackAsGuided = eventType.classes.some((c) => c.name === "trackAsGuidedExercise")
	if (hasTrackAsGuided) {
		return "guided"
	}
	const hasTrackAsOwn = eventType.classes.some((c) => c.name === "trackAsOwnExercise")
	if (hasTrackAsOwn) {
		return "own"
	}
	const hasGroup = eventType.classes.some((c) => c.name === "group")
	if (hasGroup) {
		return "guided"
	}
	const hasPersonal = eventType.classes.some((c) => c.name === "personal")
	if (hasPersonal) {
		return "own"
	}
	return "guided"
}

/**
 * Returns dictionary of own and guided event types, which are then also
 * grouped into categories in a subdictionary.
 *
 * Current expected subcategories are:
 * [sportTraining, physicalTraining, otherTraining, teamMatch]
 *
 * @param eventTypes 				Array of event types; `classes` path should be populated in backend
 * @returns 										Dictionary with nesting that terminates in array of event types belonging to a category
 */
export const getCategoryMapped = (eventTypes: EventType[]) => {
	const base: Record<string, Record<string, string[]>> = {
		own: {
			physicalTraining: [],
			sportTraining: [],
			otherTraining: [],
		},
		guided: {
			physicalTraining: [],
			sportTraining: [],
			otherTraining: [],
			teamMatch: [],
		},
	}
	const res = eventTypes.reduce((a: Record<string, Record<string, string[]>>, b) => {
		const eventType = b

		const mainPath = getExerciseMainCategory(eventType)

		const categories = eventType.classes.filter((c) => c.type === "exerciseCategory")
		if (_.isEmpty(categories)) {
			pushToPath(a, [mainPath, "otherTraining"], eventType.name)
		}
		categories.forEach((c) => {
			pushToPath(a, [mainPath, c.name], eventType.name)
		})

		return a
	}, {})
	return _.merge(base, res)
}
